/**
 * Layout — app shell composed of <Sidebar> (left), <Topbar> (top) and a
 * scrollable <main> region. Wraps every authenticated route.
 */
import type { ReactNode } from 'react';
import { Sidebar, type NavItem } from './Sidebar';
import { ToastHost } from './ToastHost';
import { Topbar, type BreadcrumbItem } from './Topbar';
import type { TenantOption } from './TenantSwitcher';
import { cn } from './cn';

export interface LayoutProps {
  children: ReactNode;
  breadcrumbs?: readonly BreadcrumbItem[];
  tenants?: readonly TenantOption[];
  allowAllTenants?: boolean;
  navItems?: readonly NavItem[];
  topbarRight?: ReactNode;
  userInitials?: string | null;
  userLabel?: string;
  hasUnread?: boolean;
  buildId?: string;
  className?: string;
}

export function Layout({
  children,
  breadcrumbs,
  tenants,
  allowAllTenants,
  navItems,
  topbarRight,
  userInitials,
  userLabel,
  hasUnread,
  buildId,
  className,
}: LayoutProps): JSX.Element {
  return (
    <div
      className={cn(
        'flex h-screen bg-surface-canvas font-sans text-fg-primary antialiased',
        className,
      )}
    >
      <Sidebar
        tenants={tenants}
        allowAllTenants={allowAllTenants}
        items={navItems}
        buildId={buildId}
      />
      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar
          breadcrumbs={breadcrumbs}
          right={topbarRight}
          userInitials={userInitials}
          userLabel={userLabel}
          hasUnread={hasUnread}
        />
        <main className="flex-1 overflow-auto" role="main">
          {children}
        </main>
      </div>
      <ToastHost />
    </div>
  );
}
